import { useState } from 'react';
import { Icon } from '../components/Icon';
import { api, ApiError } from '../lib/api';
import { parseDateOnly } from '../data/sampleData';

// Zip code lookup (Bettina, 2026-10-20). Admins and reviewers. Every client
// served in a zip over the last 90 days — a visit clocked in there or a home
// address there — on the data already in the tool. Nothing is saved.

interface Owner { af_number: string; status: string | null; state: string | null }
interface Row {
  site_number: number;
  servicing_af: string;
  client_name: string | null;
  home_zip: string | null;
  matched_on: 'both' | 'clock-in' | 'home';
  payers: string | null;
  start_of_care: string | null;
  last_visit: string | null;
  visits_in_window: number;
  visits_in_zip: number;
  client_status: string | null;
  client_status_unknown?: boolean;
  data_as_of: string | null;
  status: Status;
  status_note: string;
}
interface Result { zip: string; window_days: number; inactive_hidden?: number; owners: Owner[]; clients: Row[] }

// Servicing AF# colour (Bettina, 2026-10-20); the server decides the status.
type Status = 'own' | 'elsewhere' | 'open' | 'clear' | 'legacy';
const STATUS_STYLE: Record<Status, { color: string; label: string; detail: string }> = {
  own:       { color: 'var(--text)', label: 'Owner / unowned', detail: 'Servicing partner owns this zip, or no territory owns it' },
  open:      { color: '#dc2626', label: 'Potential encroachment', detail: 'Not addressed: open case, or the tree flags it and no case exists yet' },
  clear:     { color: '#16a34a', label: 'Clear', detail: 'Cleared by SPC/IR/FA, resolved, dismissed or suppressed' },
  legacy:    { color: '#2563eb', label: 'Legacy / grandfathered', detail: "Start of care predates the owner territory's effective date" },
  elsewhere: { color: '#ea580c', label: 'Judged elsewhere', detail: "Client's main service zip is another zip; no verdict for this one" },
};
const STATUS_ORDER: Status[] = ['own', 'open', 'clear', 'legacy', 'elsewhere'];

const MATCH_LABEL: Record<Row['matched_on'], string> = {
  both: 'Clock-in + home', 'clock-in': 'Clock-in only', home: 'Home only',
};
const day = (d: Date | null) => (d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export function ZipLookup() {
  const [zip, setZip] = useState('');
  const [result, setResult] = useState<Result | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    const z = zip.trim();
    if (!/^\d{5}$/.test(z)) { setError('Enter a 5-digit zip code.'); return; }
    setLoading(true); setError(null);
    try {
      setResult(await api<Result>(`/api/zip-lookup?zip=${z}`));
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { error?: string } | null) : null;
      setError(body?.error ?? (e instanceof Error ? e.message : 'Lookup failed'));
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const oldest = result?.clients.reduce<Date | null>((m, r) => {
    if (!r.data_as_of) return m;
    const d = new Date(r.data_as_of);
    return !m || d < m ? d : m;
  }, null);

  return (
    <div className="scroll-y" style={{ flex: 1, padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 11.5, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>Reference</div>
        <h1 style={{ margin: '4px 0 0', fontSize: 22, fontWeight: 600 }}>Zip code lookup</h1>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4, maxWidth: 820 }}>
          Every client served in a zip code over the last 90 days: a visit clocked in there, or a home address there.
          Uses the data already in the tool (no new AxisCare pull); each row shows when its site was last refreshed.
        </div>
      </div>

      <form className="card card-pad" style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 16 }}
            onSubmit={(e) => { e.preventDefault(); void run(); }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          <span className="muted">Zip code</span>
          <input value={zip} onChange={(e) => setZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
                 inputMode="numeric" placeholder="e.g. 19116" style={{ width: 140 }} className="mono" />
        </label>
        <button className="btn btn-primary" type="submit" disabled={loading || zip.length !== 5}>
          <Icon name="search" size={12} /> {loading ? 'Checking…' : 'Run check'}
        </button>
      </form>

      {error && <div className="card card-pad c-warn" style={{ fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {result && (
        <div className="card">
          <div className="card-pad" style={{ borderBottom: '1px solid var(--border)' }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
              Zip <span className="mono">{result.zip}</span> · {result.clients.length} active client{result.clients.length === 1 ? '' : 's'} in the last {result.window_days} days
            </h3>
            {(result.inactive_hidden ?? 0) > 0 && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                {result.inactive_hidden} client{result.inactive_hidden === 1 ? '' : 's'} inactive in AxisCare not shown (lookups, like audits, cover active clients only).
              </div>
            )}
            <div style={{ fontSize: 13, marginTop: 6 }}>
              {result.owners.length === 0
                ? <span className="c-warn">No territory owns this zip code.</span>
                : <>Owned by{' '}
                    {result.owners.map((o, i) => (
                      <span key={o.af_number}>
                        {i > 0 && ', '}
                        <strong className="mono">{o.af_number}</strong>
                        <span className="muted"> ({o.status ?? 'status unknown'}{o.state ? ` · ${o.state}` : ''})</span>
                      </span>
                    ))}
                    {result.owners.length > 1 && <span className="c-warn"> · more than one territory owns this zip</span>}
                  </>}
            </div>
            {oldest && (
              <div className="muted" style={{ fontSize: 12, marginTop: 4 }}>
                Oldest site data in these results: {oldest.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
              </div>
            )}
          </div>
          {result.clients.length > 0 && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 14px', borderBottom: '1px solid var(--border)', fontSize: 11.5 }}>
              <span className="muted">Servicing AF# colour:</span>
              {STATUS_ORDER.map((k) => (
                <span key={k} title={STATUS_STYLE[k].detail} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  <span className="mono" style={{ color: STATUS_STYLE[k].color, fontWeight: 600 }}>AF-###</span>
                  <span>{STATUS_STYLE[k].label}</span>
                  <span className="muted">({result.clients.filter((r) => r.status === k).length})</span>
                </span>
              ))}
              <span className="muted">· hover an AF# for the reason</span>
            </div>
          )}
          {result.clients.length === 0 ? (
            <div className="card-pad muted" style={{ fontSize: 13 }}>No clients served in this zip code in the last {result.window_days} days.</div>
          ) : (
            <table className="tbl">
              <thead><tr>
                <th>Client</th><th>Servicing AF#</th><th>Matched on</th><th>Home zip</th><th>Payer</th>
                <th>Start of care</th><th>Last visit</th>
                <th style={{ textAlign: 'right' }} title="Visits in the 90-day window (in this zip)">Visits (90d)</th>
                <th>Status</th><th>Data as of</th>
              </tr></thead>
              <tbody>
                {result.clients.map((r) => {
                  const st = STATUS_STYLE[r.status] ?? STATUS_STYLE.own;
                  return (
                    <tr key={`${r.site_number}:${r.client_name}:${r.last_visit}`}>
                      <td>{r.client_name ?? <span className="muted">(no name on file)</span>}</td>
                      <td className="mono" style={{ color: st.color, fontWeight: r.status === 'own' ? undefined : 600 }}
                          title={`${st.label}: ${r.status_note}`}>{r.servicing_af}</td>
                      <td style={{ fontSize: 12 }}>{MATCH_LABEL[r.matched_on]}</td>
                      <td className="mono">{r.home_zip ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{r.payers ?? <span className="muted">(none recorded)</span>}</td>
                      <td>{day(parseDateOnly(r.start_of_care))}</td>
                      <td>{day(r.last_visit ? new Date(r.last_visit) : null)}</td>
                      <td className="num mono" style={{ textAlign: 'right' }}>
                        {r.visits_in_window}{r.visits_in_zip !== r.visits_in_window && <span className="muted"> ({r.visits_in_zip} here)</span>}
                      </td>
                      <td>
                        {r.client_status ?? '—'}
                        {r.client_status_unknown && <span className="c-warn" style={{ fontSize: 11 }} title="No AxisCare active/inactive flag in the warehouse for this client"> · status unknown</span>}
                      </td>
                      <td className="muted" style={{ fontSize: 11.5 }}>{r.data_as_of ? new Date(r.data_as_of).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
